import React, { memo, useCallback } from 'react'
import type { SearchTab } from '../types'
import { SEARCH_TABS } from '../types'
import {
  CategoryContainer,
  CategoryHeader,
  CategoryTitle,
  ViewAllButton,
  CategoryList,
} from '../styles'

type CategorySectionProps = {
  title: string
  totalCount: number
  visibleCount: number
  targetTab: SearchTab
  onViewAll: (tab: SearchTab) => void
  children: React.ReactNode
}

export const CategorySection = memo<CategorySectionProps>(
  ({ title, totalCount, visibleCount, targetTab, onViewAll, children }) => {
    const hasMore = totalCount > visibleCount
    const showViewAll = hasMore && targetTab !== SEARCH_TABS.ALL

    const handleViewAll = useCallback(
      (e: React.MouseEvent<HTMLButtonElement>): void => {
        e.stopPropagation()
        e.currentTarget.focus()
        onViewAll(targetTab)
      },
      [onViewAll, targetTab]
    )

    const handleMouseDown = useCallback(
      (e: React.MouseEvent<HTMLButtonElement>): void => {
        e.preventDefault()
        e.stopPropagation()
        e.currentTarget.focus()
      },
      []
    )

    return (
      <CategoryContainer>
        <CategoryHeader>
          <CategoryTitle>{title}</CategoryTitle>
          {showViewAll && (
            <ViewAllButton
              type="button"
              onClick={handleViewAll}
              onMouseDown={handleMouseDown}
              aria-label={`View all ${totalCount} ${title.toLowerCase()}`}
            >
              View all {totalCount}
            </ViewAllButton>
          )}
        </CategoryHeader>
        <CategoryList role="list" aria-label={`${title} results`}>
          {children}
        </CategoryList>
      </CategoryContainer>
    )
  }
)

CategorySection.displayName = 'CategorySection'
